import React, { FunctionComponent, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import Client from '@spike/client-model';
import {
    fetchClientRewardsThunk,
    fetchLoyaltyConfigThunk,
    LoyaltyState,
    LoyaltyStatus,
    resetClientRewardsThunk
} from '@spike/loyalty-action';
import { RewardsSummary as RewardsSummaryModel } from '@spike/loyalty-model';
import { RootState } from 'store';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import Spinner from 'components/UI/V3/Spinner';
import { ClientEditHeader } from 'components/Client/ClientEditHeader';
import LoyaltyInactiveWarning from './LoyaltyInactiveWarning';
import RewardsSummary from './RewardsSummary/RewardsSummary';
import RecentActivity, { PetFilterValue } from './RecentActivity/RecentActivity';
import styles from './ClientRewards.module.scss';

// Default rewards page size; backend caps at 100.
const REWARDS_PAGE_SIZE = 10;

export interface ClientRewardsProps {
    clientId: number;
    clientRef: Client;
    onBook: () => void;
    onNewSale?: () => void;
    onClose: () => void;
    onSelect: (key: string) => void;
}

const ClientRewards: FunctionComponent<ClientRewardsProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const { config, status, rewards, rewardsStatus, rewardsCustomerId } = useSelector<RootState, LoyaltyState>(
        state => state.loyalty
    );
    const isLoyaltyActive = config?.active ?? false;

    const [page, setPage] = useState(1);
    const [selectedPetId, setSelectedPetId] = useState<PetFilterValue>('all');

    const [trackedClientId, setTrackedClientId] = useState(props.clientId);
    if (trackedClientId !== props.clientId) {
        setTrackedClientId(props.clientId);
        setPage(1);
        setSelectedPetId('all');
    }

    const petId = selectedPetId === 'all' ? undefined : selectedPetId;

    const isConfigResolved = Boolean(config) || status === LoyaltyStatus.Error;

    const isRewardsResolved =
        rewardsStatus === LoyaltyStatus.Error || (rewardsCustomerId === props.clientId && rewards !== undefined);
    const isResolved = isConfigResolved && isRewardsResolved;
    const isPageLoading = rewardsStatus === LoyaltyStatus.Fetching;

    const summary: RewardsSummaryModel = {
        pendingPoints: rewards?.summary.pendingPoints ?? 0,
        estimatedWalletValue: rewards?.summary.estimatedWalletValue ?? 0,
        lifetimePoints: rewards?.summary.lifetimePoints ?? 0,
        holdPeriodDays: config?.holdPeriodDays ?? rewards?.summary.holdPeriodDays ?? 0
    };
    const activities = rewards?.activities ?? [];

    useEffect(() => {
        (dispatch as any)(fetchLoyaltyConfigThunk(apiClientWrapper));

        return () => {
            (dispatch as any)(resetClientRewardsThunk());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.clientId]);

    // while the next result loads.
    useEffect(() => {
        (dispatch as any)(
            fetchClientRewardsThunk(apiClientWrapper, props.clientId, { page, size: REWARDS_PAGE_SIZE, petId })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.clientId, page, petId]);

    const petChangeHandler = (value: PetFilterValue) => {
        setSelectedPetId(value);
        setPage(1);
    };

    const editHeader = isMobile && (
        <div className={styles.editWrapper}>
            <ClientEditHeader
                client={props.clientRef}
                onSelect={props.onSelect}
                onBook={props.onBook}
                onNewSale={props.onNewSale}
                onClose={props.onClose}
            />
        </div>
    );

    if (!isResolved) {
        return (
            <div className={styles.root}>
                {editHeader}
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {editHeader}

            <div className={styles.content}>
                {!isLoyaltyActive && <LoyaltyInactiveWarning />}

                <h1 className={styles.title}>Rewards</h1>

                <RewardsSummary summary={summary} />

                <RecentActivity
                    activities={activities}
                    pets={props.clientRef.pets}
                    selectedPetId={selectedPetId}
                    onChangePetId={petChangeHandler}
                    pagination={rewards?.pagination}
                    isPageLoading={isPageLoading}
                    onPageChange={setPage}
                />
            </div>
        </div>
    );
};

export default ClientRewards;
